import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  barcodeKeys,
  invalidateBarcodeRelatedQueries,
  mapBarcodeError,
  normalizeBarcode,
  useAuthSession,
  useTranslation,
} from '@dukanti/shared';
import type {
  BarcodeLookupResult,
  BarcodeStockUpdateResult,
  TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useAdminBarcodeLookup, useAdminReceiveVariantStock } from '@/hooks/use-barcode-intake';
import { useQueryClient } from '@tanstack/react-query';
import {
  BarcodeLookupResultPanel,
  BarcodeScanInput,
  BarcodeStockUpdatePanel,
  type BarcodeScanInputHandle,
} from '@/features/stock/components';

interface BarcodeRouteState {
  readonly prefillBarcode?: string;
  readonly result?: BarcodeLookupResult;
  readonly openCamera?: boolean;
}

export function BarcodeIntakeRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const routeState = (location.state ?? {}) as BarcodeRouteState;

  const scanInputRef = useRef<BarcodeScanInputHandle | null>(null);
  const latestLookupRequestIdRef = useRef(0);
  const initialLookupSubmittedRef = useRef(false);
  const activeLookupAbortRef = useRef<AbortController | null>(null);
  const [latestResult, setLatestResult] = useState<BarcodeLookupResult | null>(
    routeState.result ?? null,
  );
  const [queuedBarcode, setQueuedBarcode] = useState<string | null>(null);
  const [lookupErrorKey, setLookupErrorKey] = useState<TranslationKey | null>(null);
  const [stockError, setStockError] = useState<unknown>(null);
  const [stockOpen, setStockOpen] = useState(false);

  const lookup = useAdminBarcodeLookup(storeId);
  const receiveStock = useAdminReceiveVariantStock();

  const initialBarcode = useMemo(
    () => routeState.prefillBarcode ?? searchParams.get('barcode') ?? '',
    [routeState.prefillBarcode, searchParams],
  );

  const submitLookup = useCallback(
    (rawBarcode: string) => {
      if (!storeId) {
        setQueuedBarcode(rawBarcode);
        return;
      }

      const normalized = normalizeBarcode(rawBarcode);
      if (!normalized) return;

      const requestId = latestLookupRequestIdRef.current + 1;
      latestLookupRequestIdRef.current = requestId;
      activeLookupAbortRef.current?.abort();
      const controller = new AbortController();
      activeLookupAbortRef.current = controller;
      setLookupErrorKey(null);
      setStockOpen(false);

      lookup.mutate(
        { rawBarcode, requestId, signal: controller.signal },
        {
          onSuccess: (result, variables) => {
            if (variables.requestId !== latestLookupRequestIdRef.current) return;
            setLatestResult(result);
            if (result.status !== 'invalid') {
              queryClient.setQueryData(barcodeKeys.lookup(storeId, result.normalized), result);
            }
          },
          onError: (err, variables) => {
            if (variables.requestId !== latestLookupRequestIdRef.current) return;
            setLookupErrorKey(mapBarcodeError(err) as TranslationKey);
          },
        },
      );
    },
    [lookup, queryClient, storeId],
  );

  useEffect(() => {
    if (!storeId || !initialBarcode || initialLookupSubmittedRef.current) return;
    initialLookupSubmittedRef.current = true;
    submitLookup(initialBarcode);
  }, [initialBarcode, storeId, submitLookup]);

  useEffect(() => {
    if (!storeId || !queuedBarcode) return;
    const nextBarcode = queuedBarcode;
    setQueuedBarcode(null);
    submitLookup(nextBarcode);
  }, [queuedBarcode, storeId, submitLookup]);

  useEffect(() => {
    return () => activeLookupAbortRef.current?.abort();
  }, []);

  const handleCreateProduct = (barcode: string, deletedReuseConfirmedNormalized?: string) => {
    navigate(`/products/new?barcode=${encodeURIComponent(barcode)}`, {
      state: { prefillBarcode: barcode, deletedReuseConfirmedNormalized },
    });
  };

  const handleStockSuccess = (result: BarcodeStockUpdateResult) => {
    setStockError(null);
    setStockOpen(false);
    toast.success(t('admin.barcode.stock.success'));
    const known = latestResult?.status === 'known' ? latestResult : null;
    if (known && storeId) {
      setLatestResult({
        ...known,
        variant: { ...known.variant, inventory: result.inventoryAfter },
      });
      void invalidateBarcodeRelatedQueries(queryClient, {
        storeId,
        productId: result.productId,
        variantId: result.variantId,
        normalized: known.normalized,
      });
    }
    scanInputRef.current?.focus();
  };

  const knownVariant = latestResult?.status === 'known' ? latestResult.variant : null;

  return (
    <main
      dir={dir}
      data-testid="barcode-intake-route"
      className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <header className="mb-5 text-start">
        <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
          {t('admin.barcode.scan.title')}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">{t('admin.barcode.scan.subtitle')}</p>
      </header>

      <div className="space-y-4">
        <BarcodeScanInput
          ref={scanInputRef}
          defaultValue={initialBarcode}
          initialCameraOpen={routeState.openCamera === true}
          pending={lookup.isPending}
          onSubmit={submitLookup}
        />
        {lookupErrorKey ? (
          <p role="alert" className="text-destructive text-start text-sm">
            {t(lookupErrorKey)}
          </p>
        ) : null}
        <BarcodeLookupResultPanel
          result={latestResult}
          onEditProduct={(productId) => navigate(`/products/${productId}/edit`)}
          onCreateProduct={handleCreateProduct}
          onUpdateStock={() => setStockOpen(true)}
          onScanAnother={() => {
            setLatestResult(null);
            setStockOpen(false);
            scanInputRef.current?.focus();
          }}
        />
        {stockOpen && knownVariant ? (
          <BarcodeStockUpdatePanel
            variant={knownVariant}
            isSubmitting={receiveStock.isPending}
            serviceError={stockError}
            onSubmit={async (input) => {
              if (!('variantId' in input)) {
                throw Object.assign(new Error('missing variant id'), { code: 'P0002' });
              }
              try {
                const result = await receiveStock.mutateAsync(input);
                return result;
              } catch (err) {
                setStockError(err);
                return undefined;
              }
            }}
            onSuccess={handleStockSuccess}
          />
        ) : null}
      </div>
    </main>
  );
}
